import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@tremor/react';
import { ArrowLeft, Save, Trash } from 'lucide-react';
import { CodeEditor } from '../ai/CodeEditor';

const CONDITION_OPERATORS = [
  { value: ">", label: "Greater than" },
  { value: "<", label: "Less than" },
  { value: "==", label: "Equals" },
  { value: "!=", label: "Not equals" },
  { value: ">=", label: "Greater than or equal" },
  { value: "<=", label: "Less than or equal" },
  { value: "contains", label: "Contains" },
];

const CONDITION_FIELDS = [
  { value: "orderAmount", label: "Order Amount", type: "NUMBER" },
  { value: "user.loyaltyTier", label: "User Loyalty Tier", type: "STRING" },
  { value: "message.intent", label: "Message Intent", type: "STRING" },
  { value: "daysSincePurchase", label: "Days Since Purchase", type: "NUMBER" },
];

export function PolicyDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isNew = id === 'new';

  const [formData, setFormData] = useState({
    name: "",
    description: "",
    condition: {
      field: "",
      operator: "",
      value: "",
    },
    action: {
      type: "skill",
      skillId: "",
      parameters: {},
    },
    priority: 0,
    isActive: true,
  });

  const [showTestPanel, setShowTestPanel] = useState(false);
  const [testContext, setTestContext] = useState({
    orderAmount: 0,
    user: { loyaltyTier: "STANDARD" },
    message: { intent: "" },
    daysSincePurchase: 0,
  });
  const [testResult, setTestResult] = useState(null);

  const { data: policyData, isLoading: policyLoading, error: policyError } = useQuery({
    queryKey: ['policy', id],
    queryFn: async () => {
      const response = await fetch(`/api/policies/${id}`);
      if (!response.ok) {
        throw new Error('Failed to fetch policy');
      }
      return response.json();
    },
    enabled: !isNew
  });

  const { data: skillsData, isLoading: skillsLoading } = useQuery({
    queryKey: ['skills'],
    queryFn: async () => {
      const response = await fetch('/api/skills');
      if (!response.ok) {
        throw new Error('Failed to fetch skills');
      }
      return response.json();
    }
  });

  const updatePolicyMutation = useMutation({
    mutationFn: async (variables) => {
      const response = await fetch(`/api/policies/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(variables)
      });
      if (!response.ok) {
        throw new Error('Failed to update policy');
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['policy', id]);
      navigate('/admin/policies');
    }
  });

  const deletePolicyMutation = useMutation({
    mutationFn: async (variables) => {
      const response = await fetch(`/api/policies/${id}`, {
        method: 'DELETE'
      });
      if (!response.ok) {
        throw new Error('Failed to delete policy');
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['policies']);
      navigate('/admin/policies');
    }
  });

  useEffect(() => {
    if (policyData?.policy) {
      setFormData(policyData.policy);
    }
  }, [policyData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await updatePolicyMutation.mutateAsync(formData);
    } catch (error) {
      console.error('Error saving policy:', error);
    }
  };

  const handleDelete = async () => {
    if (window.confirm('Are you sure you want to delete this policy?')) {
      try {
        await deletePolicyMutation.mutateAsync({ id });
      } catch (error) {
        console.error('Error deleting policy:', error);
      }
    }
  };

  const handleTestPolicy = () => {
    // TODO: Implement policy testing
    setShowTestPanel(true);
  };

  if (policyLoading) {
    return <div>Loading...</div>;
  }

  if (policyError) {
    return <div>Error: {policyError.message}</div>;
  }

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <Button
            variant="secondary"
            onClick={() => navigate(-1)}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <h1 className="text-2xl font-bold">
            {isNew ? 'Create Policy' : 'Edit Policy'}
          </h1>
        </div>
        {!isNew && (
          <Button
            variant="destructive"
            onClick={handleDelete}
          >
            <Trash className="h-4 w-4 mr-2" />
            Delete Policy
          </Button>
        )}
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div>
          <label className="block text-sm font-medium text-gray-700">
            Name
          </label>
          <input
            type="text"
            value={formData.name}
            onChange={(e) =>
              setFormData((prev) => ({ ...prev, name: e.target.value }))
            }
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">
            Description
          </label>
          <textarea
            value={formData.description}
            onChange={(e) =>
              setFormData((prev) => ({
                ...prev,
                description: e.target.value,
              }))
            }
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
            rows="3"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Condition
          </label>
          <div className="flex space-x-2">
            <select
              value={formData.condition.field}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  condition: {
                    ...prev.condition,
                    field: e.target.value,
                    value: "",
                  },
                }))
              }
              className="mt-1 block w-1/3 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
              required
            >
              <option value="">Select field</option>
              {CONDITION_FIELDS.map((field) => (
                <option key={field.value} value={field.value}>
                  {field.label}
                </option>
              ))}
            </select>

            <select
              value={formData.condition.operator}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  condition: {
                    ...prev.condition,
                    operator: e.target.value,
                  },
                }))
              }
              className="mt-1 block w-1/3 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
              required
            >
              <option value="">Select operator</option>
              {CONDITION_OPERATORS.map((op) => (
                <option key={op.value} value={op.value}>
                  {op.label}
                </option>
              ))}
            </select>

            <input
              type={CONDITION_FIELDS.find(f => f.value === formData.condition.field)?.type === "number" ? "number" : "text"}
              value={formData.condition.value}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  condition: {
                    ...prev.condition,
                    value:
                      CONDITION_FIELDS.find(f => f.value === prev.condition.field)?.type === "number"
                        ? parseFloat(e.target.value)
                        : e.target.value,
                  },
                }))
              }
              className="mt-1 block w-1/3 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
              placeholder="Value"
              required
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Action
          </label>
          <div className="space-y-4">
            <select
              value={formData.action.skillId}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  action: {
                    ...prev.action,
                    skillId: e.target.value,
                    parameters: {},
                  },
                }))
              }
              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
              required
            >
              <option value="">Select skill</option>
              {skillsData?.skills.map((skill) => (
                <option key={skill.id} value={skill.id}>
                  {skill.name}
                </option>
              ))}
            </select>

            {skillsData?.skills.find(s => s.id === formData.action.skillId) && (
              <div className="space-y-4">
                <div className="text-sm text-gray-600">
                  {skillsData.skills.find(s => s.id === formData.action.skillId).description}
                </div>
                {skillsData.skills.find(s => s.id === formData.action.skillId).parameters.map((param) => (
                  <div key={param.name}>
                    <label className="block text-sm font-medium text-gray-700">
                      {param.name}
                      {param.required && (
                        <span className="text-red-500 ml-1">*</span>
                      )}
                    </label>
                    <input
                      type={param.type === "number" ? "number" : "text"}
                      value={formData.action.parameters[param.name] || ""}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          action: {
                            ...prev.action,
                            parameters: {
                              ...prev.action.parameters,
                              [param.name]:
                                param.type === "number"
                                  ? parseFloat(e.target.value)
                                  : e.target.value,
                            },
                          },
                        }))
                      }
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                      required={param.required}
                    />
                    {param.description && (
                      <p className="mt-1 text-sm text-gray-500">
                        {param.description}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="flex justify-between items-center">
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Priority
            </label>
            <input
              type="number"
              value={formData.priority}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  priority: parseInt(e.target.value),
                }))
              }
              className="mt-1 block w-32 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
              min="0"
              required
            />
          </div>

          <div className="flex items-center">
            <input
              type="checkbox"
              checked={formData.isActive}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  isActive: e.target.checked,
                }))
              }
              className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
            />
            <span className="ml-2 text-sm text-gray-700">Active</span>
          </div>
        </div>

        <div>
          <Button
            type="button"
            variant="secondary"
            onClick={() => setShowTestPanel(!showTestPanel)}
          >
            {showTestPanel ? "Hide Test Panel" : "Show Test Panel"}
          </Button>

          {showTestPanel && (
            <div className="mt-4 p-4 border rounded-lg space-y-4">
              <h3 className="font-medium">Test Policy</h3>
              <div className="space-y-2">
                <label className="block text-sm text-gray-700">
                  Test Context (JSON)
                </label>
                <CodeEditor
                  value={JSON.stringify(testContext, null, 2)}
                  onChange={(value) => {
                    try {
                      setTestContext(JSON.parse(value));
                      setTestResult(null);
                    } catch (e) {
                      // Invalid JSON, ignore
                    }
                  }}
                  language="json"
                />
              </div>

              <Button onClick={handleTestPolicy}>Test Policy</Button>

              {testResult && (
                <div
                  className={`mt-4 p-4 rounded-lg ${
                    testResult.error
                      ? "bg-red-50 text-red-700"
                      : testResult.conditionMet
                      ? "bg-green-50 text-green-700"
                      : "bg-yellow-50 text-yellow-700"
                  }`}
                >
                  {testResult.error ? (
                    <div>{testResult.error}</div>
                  ) : (
                    <div>
                      <div>
                        Condition{" "}
                        {testResult.conditionMet ? "matched" : "did not match"}
                      </div>
                      {testResult.conditionMet && (
                        <div className="mt-2">
                          <div className="font-medium">Action to execute:</div>
                          <pre className="mt-1 text-sm">
                            {JSON.stringify(testResult.action, null, 2)}
                          </pre>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </form>
    </div>
  );
} 